import ColorPicker from "components/colorPicker/ColorPicker";
import RadioGroup from "components/radioGroup/RadioGroup";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import Modal from "react-modal";
import tinycolor from "tinycolor2";

type Theme = "light" | "dark" | "custom" | null;

const cssVars = [
  "font-color",
  "link-color",
  "background-color",
  "border-color",
  "button-color",
  "good-lift",
  "bad-lift",
  "current-lift",
] as const;

type CssVars = Record<(typeof cssVars)[number], string>;

const defaultDarkVars: CssVars = {
  "font-color": "#f0f0f0",
  "link-color": "#ffffff",
  "background-color": "#000000",
  "border-color": "#a9a9a9",
  "button-color": "#444444",
  "good-lift": "#006600",
  "bad-lift": "#cc0000",
  "current-lift": "#333366",
};

const defaultLightVars: CssVars = {
  "font-color": "#101010",
  "link-color": "#000000",
  "background-color": "#ffffff",
  "border-color": "#444444",
  "button-color": "#aaaaaa",
  "good-lift": "#008800",
  "bad-lift": "#ff1a1a",
  "current-lift": "#7575a6",
};

const themeOptions = [
  { label: "Dark Mode", value: "dark" },
  { label: "Light Mode", value: "light" },
  { label: "Custom Theme", value: "custom" },
];

const isReadable = (
  color1: string | undefined | null,
  color2: string | undefined | null
) => {
  if (!color1 || !color2) {
    return false;
  }
  return tinycolor.isReadable(color1, color2, { level: "AA", size: "large" });
};

const secondaryContrastCheck = (
  color1: string | undefined | null,
  color2: string | undefined | null
) => {
  if (!color1 || !color2) {
    return false;
  }
  return tinycolor.readability(color1, color2) > 1.2;
};

const isValidHexColor = (hexCode: string | undefined | null) => {
  if (!hexCode) {
    return false;
  }
  const hexPattern = /^#([a-fA-F0-9]{3}|[a-fA-F0-9]{6})$/;

  return hexPattern.test(hexCode);
};

const bulkEditStringify = (vars: CssVars) => {
  return cssVars.map((cssVar) => `${cssVar}:${vars[cssVar]}`).join("\n");
};

const bulkEditParse = (text: string) => {
  const newCssVars: Partial<CssVars> = {};
  const lines = text.split("\n");
  lines.forEach((line) => {
    const parts = line.split(":");
    const key = parts.at(0)?.trim() as (typeof cssVars)[number];
    const value = parts.at(1)?.trim();
    if (key && value && cssVars.includes(key)) {
      newCssVars[key] = value;
    }
  });
  return newCssVars;
};

const validateVars = (vars: Partial<CssVars>) => {
  const errors: string[] = [];

  cssVars.forEach((key) => {
    if (!vars[key]) {
      errors.push(`${key} is missing.`);
    }
  });

  cssVars.forEach((key) => {
    if (vars[key] && !isValidHexColor(vars[key])) {
      errors.push(`${key} is not a valid hex value.`);
    }
  });

  if (errors.length) {
    return errors.join(" ");
  }

  if (!isReadable(vars["font-color"], vars["background-color"])) {
    errors.push(
      "Insufficient contrast between font-color and background-color."
    );
  }
  if (!isReadable(vars["font-color"], vars["button-color"])) {
    errors.push("Insufficient contrast between font-color and button-color.");
  }
  if (!isReadable(vars["link-color"], vars["background-color"])) {
    errors.push(
      "Insufficient contrast between link-color and background-color."
    );
  }

  if (!isReadable(vars["font-color"], vars["good-lift"])) {
    errors.push("Insufficient contrast between font-color and good-lift.");
  }
  if (!isReadable(vars["font-color"], vars["bad-lift"])) {
    errors.push("Insufficient contrast between font-color and bad-lift.");
  }
  if (!isReadable(vars["font-color"], vars["current-lift"])) {
    errors.push("Insufficient contrast between font-color and current-lift.");
  }

  if (!secondaryContrastCheck(vars["background-color"], vars["good-lift"])) {
    errors.push(
      "Insufficient contrast between background-color and good-lift."
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], vars["bad-lift"])) {
    errors.push("Insufficient contrast between background-color and bad-lift.");
  }
  if (!secondaryContrastCheck(vars["background-color"], vars["current-lift"])) {
    errors.push(
      "Insufficient contrast between background-color and current-lift."
    );
  }

  if (!secondaryContrastCheck(vars["background-color"], "#cc0000")) {
    errors.push(
      "Insufficient contrast between background-color and system red."
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], "#006600")) {
    errors.push(
      "Insufficient contrast between background-color and system green."
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], "#cccc00")) {
    errors.push(
      "Insufficient contrast between background-color and system yellow."
    );
  }
  if (!secondaryContrastCheck(vars["background-color"], "#0000cc")) {
    errors.push(
      "Insufficient contrast between background-color and system blue."
    );
  }

  return errors.join(" ");
};

const ThemeContext = createContext<null | {
  theme: Theme;
  openThemeModal: () => void;
}>(null);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const storedTheme = localStorage.getItem("theme");
      if (
        storedTheme === "light" ||
        storedTheme === "dark" ||
        storedTheme === "custom"
      ) {
        return storedTheme;
      }
    } catch (e) {
      //
    }

    return "dark";
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [bulkEditText, setBulkEditText] = useState("");
  const [bulkEditErrors, setBulkEditErrors] = useState("");

  const [customTheme, setCustomTheme] = useState<CssVars>(() => {
    try {
      const storedCustomThemeString = localStorage.getItem("customTheme");
      if (storedCustomThemeString) {
        const storedCustomThemeObject = JSON.parse(storedCustomThemeString);
        if (!validateVars(storedCustomThemeObject)) {
          return storedCustomThemeObject;
        }
      }
    } catch (e) {
      // console.error("Failed to read custom theme", e);
    }
    return defaultDarkVars;
  });

  const openThemeModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  const closeThemeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onRadioSelect = (value: Theme) => {
    setTheme(value);
  };

  useEffect(() => {
    if (theme === "light" || theme === "dark" || theme === "custom") {
      document.documentElement.setAttribute("data-theme", theme);
    } else {
      document.documentElement.removeAttribute("data-theme");
    }

    try {
      const storedTheme = localStorage.getItem("theme");
      if (theme && storedTheme !== theme) {
        localStorage.setItem("theme", theme);
      }
    } catch (e) {
      console.log("Failed to set theme provider in localStorage", e);
    }
  }, [theme]);

  useEffect(() => {
    try {
      if (theme === "custom" && !validateVars(customTheme)) {
        const style = cssVars
          .map((cssVar) =>
            cssVar ? `--${cssVar}:${customTheme[cssVar]};` : ""
          )
          .join("");

        document.documentElement.setAttribute("style", style);

        if (customTheme) {
          try {
            localStorage.setItem("customTheme", JSON.stringify(customTheme));
          } catch (e) {
            console.error("Failed to store custom theme", e);
          }
        }
      } else {
        document.documentElement.removeAttribute("style");
      }
    } catch (e) {
      console.error("Failed to update custom theme style attribute", e);
    }
  }, [customTheme, theme]);

  const saveBulkEdit = () => {
    const newVars = bulkEditParse(bulkEditText);
    const errors = validateVars(newVars);
    if (errors) {
      setBulkEditErrors(errors);
      return;
    }
    setBulkEditErrors("");
    setCustomTheme(newVars as CssVars);
    setBulkEditMode(false);
  };

  return (
    <ThemeContext.Provider value={{ theme, openThemeModal }}>
      <Modal
        ariaHideApp={false}
        isOpen={modalOpen}
        contentLabel="Theme Settings"
        onRequestClose={closeThemeModal}
        className={{
          base: "modal-base",
          afterOpen: "",
          beforeClose: "",
        }}
        overlayClassName={{
          base: "modal-overlay",
          afterOpen: "",
          beforeClose: "",
        }}
      >
        <div className="content" style={{ minWidth: 300 }}>
          {bulkEditMode ? (
            <div>
              <textarea
                value={bulkEditText}
                style={{ height: 240 }}
                onChange={(event) => {
                  setBulkEditErrors("");
                  setBulkEditText(event.target.value);
                }}
              />
            </div>
          ) : (
            <>
              <RadioGroup
                onCheck={onRadioSelect}
                value={theme ?? "dark"}
                items={themeOptions}
              />
              {theme === "custom" && (
                <>
                  <div style={{ marginBottom: 20 }}>
                    {cssVars.map((cssVar) => {
                      return (
                        <div key={cssVar}>
                          <label
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <div style={{ minWidth: 160 }}>{cssVar}: </div>
                            <ColorPicker
                              onChange={(v) => {
                                if (v) {
                                  setCustomTheme((prev) => ({
                                    ...prev,
                                    [cssVar]: v,
                                  }));
                                }
                              }}
                              value={customTheme[cssVar]}
                              validate={(v) => {
                                const proposedVars = {
                                  ...customTheme,
                                  [cssVar]: v,
                                };
                                return validateVars(proposedVars);
                              }}
                            />
                          </label>
                        </div>
                      );
                    })}
                  </div>
                  <div>
                    <button
                      onClick={() => {
                        setCustomTheme(defaultDarkVars);
                      }}
                    >
                      Reset To Dark Mode Defaults
                    </button>
                    <button
                      style={{ marginTop: 12 }}
                      onClick={() => {
                        setCustomTheme(defaultLightVars);
                      }}
                    >
                      Reset To Light Mode Defaults
                    </button>
                    <button
                      style={{ marginTop: 12 }}
                      onClick={() => {
                        setBulkEditText(bulkEditStringify(customTheme));
                        setBulkEditMode(true);
                      }}
                    >
                      Bulk Edit Mode
                    </button>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="button-row">
          {bulkEditMode ? (
            <>
              <button
                style={{ marginRight: 12 }}
                onClick={() => {
                  setBulkEditErrors("");
                  setBulkEditMode(false);
                }}
              >
                Cancel
              </button>
              <button onClick={saveBulkEdit}>Save</button>
            </>
          ) : (
            <button onClick={closeThemeModal}>Close</button>
          )}
        </div>
        {theme === "custom" && (
          <div
            style={{
              maxWidth: 300,
              minHeight: 100,
              padding: 12,
            }}
          >
            {!!bulkEditErrors && (
              <div
                style={{
                  backgroundColor: "black",
                  borderRadius: 4,
                  color: "red",

                  maxWidth: 300,
                  padding: 12,
                }}
              >
                {bulkEditErrors}
              </div>
            )}
          </div>
        )}
      </Modal>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
